import { useCallback, useEffect, useMemo, useState } from "react"

import { ChevronDown, ChevronUp, Pencil, Sparkles, Trash2 } from "@/components/icons"
import { useTranslation } from "react-i18next"

import type { DashboardSpec } from "../dashboard/catalog"

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Popover, PopoverAnchor, PopoverContent } from "@/components/ui/popover"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"

import { useDeferredSpecCommit } from "../hooks/use-deferred-spec-commit"
import { MANUAL_TILE_TYPES, MANUAL_TILE_TYPE_I18N_KEYS } from "../utils/manual-tile"
import { getFlatTiles, rebuildChildren, transformTileType } from "../utils/tile-helpers"

type TileEditPopoverProps = {
  spec: DashboardSpec
  selectedTileIndex: number
  onSpecChange: (spec: DashboardSpec) => void
  onDeselect: () => void
  /**
   * Edit-with-AI handler. When called without a description, the parent
   * is expected to surface a hint in the composer (legacy behavior).
   * When called WITH a description, the parent dispatches a structured
   * yaml-patch edit request to the agent for surgical tile-level changes
   * — see Feature #3 in `dash-sql-agent`.
   */
  onEditWithAI: (description?: string) => void
  anchorRef?: HTMLDivElement
  children: React.ReactNode
}

export function TileEditPopover({
  spec,
  selectedTileIndex,
  onSpecChange,
  onDeselect,
  onEditWithAI,
  anchorRef,
  children,
}: TileEditPopoverProps) {
  const { t } = useTranslation()
  const flatTiles = getFlatTiles(spec.children)
  const tileInfo = flatTiles.at(selectedTileIndex)
  const isTopLevelTile = tileInfo?.parentArray === spec.children

  const [title, setTitle] = useState("")
  const [tileType, setTileType] = useState("")
  const [tileWidth, setTileWidth] = useState<"full" | "half">("full")
  const [aiExpanded, setAiExpanded] = useState(false)
  const [aiDescription, setAiDescription] = useState("")
  const [removeConfirmOpen, setRemoveConfirmOpen] = useState(false)
  const virtualAnchorRef = useMemo(() => ({ current: anchorRef! }), [anchorRef])

  // §F — debounce + startTransition the parent spec commit so each
  // keystroke updates the local input synchronously but only ripples
  // through the full dashboard re-render at typing-pause boundaries.
  // commitNow() flushes pending edits before any action that needs
  // them in the parent (close, move, type-switch).
  const deferredCommit = useDeferredSpecCommit(onSpecChange)

  useEffect(() => {
    if (!tileInfo) return
    const el = tileInfo.element
    setTitle((el as { title?: string }).title ?? "")
    setTileType(el.type)
    setTileWidth((el as { width?: "full" | "half" }).width ?? "full")
  }, [tileInfo])

  const handleTitleChange = useCallback(
    (newTitle: string) => {
      setTitle(newTitle)
      if (!tileInfo) return
      const updated = { ...tileInfo.element, title: newTitle }
      const newParent = [...tileInfo.parentArray]
      newParent[tileInfo.indexInParent] = updated
      // Schedule the parent commit; debounced so rapid typing doesn't
      // re-render the whole dashboard on every keystroke.
      deferredCommit.scheduleCommit({
        ...spec,
        children: rebuildChildren(spec.children, tileInfo.parentArray, newParent),
      })
    },
    [tileInfo, spec, deferredCommit]
  )

  const handleTypeChange = useCallback(
    (newType: string) => {
      setTileType(newType)
      if (!tileInfo) return
      // Flush pending title edits first — type change rebuilds the
      // element and we'd otherwise lose buffered title typing.
      deferredCommit.commitNow()
      const updated = transformTileType(tileInfo.element, tileInfo.element.type, newType)
      const newParent = [...tileInfo.parentArray]
      newParent[tileInfo.indexInParent] = updated
      onSpecChange({
        ...spec,
        children: rebuildChildren(spec.children, tileInfo.parentArray, newParent),
      })
    },
    [tileInfo, spec, onSpecChange, deferredCommit]
  )

  const handleWidthChange = useCallback(
    (newWidth: "full" | "half") => {
      setTileWidth(newWidth)
      if (!tileInfo) return
      deferredCommit.commitNow()
      const updated = { ...tileInfo.element, width: newWidth }
      const newParent = [...tileInfo.parentArray]
      newParent[tileInfo.indexInParent] = updated
      onSpecChange({
        ...spec,
        children: rebuildChildren(spec.children, tileInfo.parentArray, newParent),
      })
    },
    [tileInfo, spec, onSpecChange, deferredCommit]
  )

  const handleRemove = useCallback(() => {
    if (!tileInfo) return
    // Pending title edits on a tile that's about to be removed are
    // intentionally dropped; cancel rather than flush so we don't
    // commit a write that will immediately be overwritten by the
    // remove anyway.
    deferredCommit.cancel()
    const newParent = tileInfo.parentArray.filter((_, i) => i !== tileInfo.indexInParent)
    onSpecChange({
      ...spec,
      children: rebuildChildren(spec.children, tileInfo.parentArray, newParent),
    })
    onDeselect()
  }, [tileInfo, spec, onSpecChange, onDeselect, deferredCommit])

  const handleMoveUp = useCallback(() => {
    if (!tileInfo || tileInfo.indexInParent <= 0) return
    deferredCommit.commitNow()
    const newParent = [...tileInfo.parentArray]
    const idx = tileInfo.indexInParent
    ;[newParent[idx - 1], newParent[idx]] = [newParent[idx], newParent[idx - 1]]
    onSpecChange({
      ...spec,
      children: rebuildChildren(spec.children, tileInfo.parentArray, newParent),
    })
    onDeselect()
  }, [tileInfo, spec, onSpecChange, onDeselect, deferredCommit])

  const handleMoveDown = useCallback(() => {
    if (!tileInfo || tileInfo.indexInParent >= tileInfo.parentArray.length - 1) return
    deferredCommit.commitNow()
    const newParent = [...tileInfo.parentArray]
    const idx = tileInfo.indexInParent
    ;[newParent[idx], newParent[idx + 1]] = [newParent[idx + 1], newParent[idx]]
    onSpecChange({
      ...spec,
      children: rebuildChildren(spec.children, tileInfo.parentArray, newParent),
    })
    onDeselect()
  }, [tileInfo, spec, onSpecChange, onDeselect, deferredCommit])

  if (!tileInfo || !anchorRef) return <>{children}</>

  return (
    <>
      <Popover
        open
        onOpenChange={(open) => {
          if (open) return
          // User-initiated close (outside click / escape) — flush pending
          // title edits BEFORE deselecting so they aren't lost. The hook
          // cancels on unmount as a defensive last resort, so without
          // this explicit flush a fast typist would lose their last
          // few keystrokes when clicking outside.
          deferredCommit.commitNow()
          onDeselect()
        }}
      >
        <PopoverAnchor virtualRef={virtualAnchorRef} />
        {children}
        <PopoverContent
          align="start"
          className="w-72"
          collisionPadding={12}
          onInteractOutside={(event) => {
            if (removeConfirmOpen) event.preventDefault()
          }}
          side="bottom"
          sideOffset={8}
        >
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-2">
              <Pencil className="text-muted-foreground size-4" />
              <h4 className="text-sm font-medium">{t("dash.tile_edit.title")}</h4>
            </div>

            <Input
              className="h-8 text-sm"
              placeholder={t("dash.dashboard.save_title")}
              value={title}
              onChange={(e) => handleTitleChange(e.target.value)}
            />

            <div className="flex flex-col gap-1.5">
              <label className="text-muted-foreground text-xs">
                {t("dash.tile_edit.change_type")}
              </label>
              <Select value={tileType} onValueChange={handleTypeChange}>
                <SelectTrigger className="h-8 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MANUAL_TILE_TYPES.map((type) => (
                    <SelectItem key={type} value={type}>
                      {t(MANUAL_TILE_TYPE_I18N_KEYS[type])}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {isTopLevelTile && (
              <div className="flex flex-col gap-1.5">
                <label className="text-muted-foreground text-xs">
                  {t("dash.add_tile.manual.width")}
                </label>
                <Select
                  value={tileWidth}
                  onValueChange={(value) => handleWidthChange(value as "full" | "half")}
                >
                  <SelectTrigger className="h-8 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="full">{t("dash.add_tile.manual.width_full")}</SelectItem>
                    <SelectItem value="half">{t("dash.add_tile.manual.width_half")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="flex gap-2">
              <Button
                className="flex-1"
                disabled={tileInfo.indexInParent <= 0}
                size="sm"
                variant="outline"
                onClick={handleMoveUp}
              >
                <ChevronUp className="mr-1.5 size-3.5" />
                {t("dash.tile_edit.move_up")}
              </Button>
              <Button
                className="flex-1"
                disabled={tileInfo.indexInParent >= tileInfo.parentArray.length - 1}
                size="sm"
                variant="outline"
                onClick={handleMoveDown}
              >
                <ChevronDown className="mr-1.5 size-3.5" />
                {t("dash.tile_edit.move_down")}
              </Button>
            </div>

            <div className="flex gap-2">
              <Button
                className="flex-1"
                size="sm"
                variant={aiExpanded ? "default" : "outline"}
                onClick={() => setAiExpanded((v) => !v)}
              >
                <Sparkles className="mr-1.5 size-3.5" />
                {t("dash.tile_edit.edit_with_ai")}
              </Button>
              <Button size="sm" variant="destructive" onClick={() => setRemoveConfirmOpen(true)}>
                <Trash2 className="mr-1.5 size-3.5" />
                {t("dash.tile_edit.remove")}
              </Button>
            </div>

            {aiExpanded && (
              <div className="border-primary/30 bg-primary/[0.03] flex flex-col gap-2 rounded-md border p-2">
                <p className="text-muted-foreground text-xs">
                  {t(
                    "dash.tile_edit.ai_hint",
                    'Beskriv ändringen i naturligt språk (t.ex. "gör den röd", "byt till LineChart", "filtrera på senaste 7 dagarna").'
                  )}
                </p>
                <Input
                  autoFocus
                  value={aiDescription}
                  placeholder={t("dash.tile_edit.ai_placeholder", {
                    defaultValue: "T.ex. byt färg till röd",
                  })}
                  onChange={(e) => setAiDescription(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault()
                      const desc = aiDescription.trim()
                      if (desc) {
                        onEditWithAI(desc)
                        setAiDescription("")
                        setAiExpanded(false)
                      }
                    }
                  }}
                />
                <div className="flex justify-end gap-1.5">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setAiExpanded(false)
                      setAiDescription("")
                    }}
                  >
                    {t("common.cancel", { defaultValue: "Avbryt" })}
                  </Button>
                  <Button
                    disabled={!aiDescription.trim()}
                    size="sm"
                    onClick={() => {
                      const desc = aiDescription.trim()
                      if (desc) {
                        onEditWithAI(desc)
                        setAiDescription("")
                        setAiExpanded(false)
                      }
                    }}
                  >
                    <Sparkles className="mr-1.5 size-3.5" />
                    {t("dash.tile_edit.ai_send", { defaultValue: "Skicka" })}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </PopoverContent>
      </Popover>

      <AlertDialog open={removeConfirmOpen} onOpenChange={setRemoveConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("dash.tile_edit.remove_title")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("dash.tile_edit.remove_description")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleRemove}
            >
              {t("dash.tile_edit.remove")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
